"use client";

import React, { useState } from "react";
import { ShieldCheck, Layers, FileText, CircleCheck, ArrowRight, Info, Microscope, Gauge } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface MaterialSpec {
  id: string;
  name: string;
  grade: string;
  unsNumber: string;
  category: "austenitic" | "nickel" | "specialty";
  prenScore: string;
  corrosionRating: Record<Lang, string>;
  keyFeatures: Record<Lang, string[]>;
  commonWeaves: Record<Lang, string[]>;
  primaryApplications: Record<Lang, string[]>;
  chemicalComposition: {
    element: string;
    minMax: string;
  }[];
  imageSrc: string;
  imageSrcSet: string;
  imageSizes: string;
  altText: Record<Lang, string>;
}
const MATERIALS_DATA: MaterialSpec[] = [{
  id: "316l",
  name: "Stainless Steel 316L",
  grade: "AISI 316L / 1.4404",
  unsNumber: "UNS S31603",
  category: "austenitic",
  prenScore: "~25",
  corrosionRating: {
    en: "Superior",
    zh: "卓越",
    es: "Superior",
    de: "Hervorragend",
    fr: "Supérieur",
    pt: "Superior"
  },
  keyFeatures: {
    en: ["Low carbon prevents intergranular corrosion", "Molybdenum alloyed for chloride resistance", "Sanitary-grade corrosion resistance for food & pharmaceutical use"],
    zh: ["超低碳含量有效防止焊接晶间腐蚀", "添加 2-3% 钼元素增强耐氯化物点蚀性能", "食品医药级卫生防腐蚀标准"],
    es: ["Bajo contenido de carbono que previene la corrosión intergranular", "Aleado con molibdeno para mayor resistencia a cloruros", "Resistencia higiénica para uso alimentario y farmacéutico"],
    de: ["Niedriger Kohlenstoffgehalt verhindert interkristalline Korrosion", "Molybdänlegierung für hohe Chloridbeständigkeit", "Hygienische Korrosionsbeständigkeit für Lebensmittel und Pharma"],
    fr: ["Faible teneur en carbone évitant la corrosion intergranulaire", "Allié au molybdène pour une haute résistance aux chlorures", "Résistance sanitaire pour l'agroalimentaire et la pharmacie"],
    pt: ["Baixo carbono que previne a corrosão intergranular", "Ligado ao molibdênio para resistência a cloretos", "Resistência sanitária para uso alimentício e farmacêutico"]
  },
  commonWeaves: {
    en: ["Plain Weave", "Twilled Weave", "Plain Dutch", "5-Layer Sintered"],
    zh: ["平纹编织", "斜纹编织", "平纹荷兰编织", "5层烧结网"],
    es: ["Tejido liso", "Tejido sarga", "Dutch liso", "Sinterizado 5 capas"],
    de: ["Leinwandbindung", "Köperbindung", "Glatte Tresse", "5-Lagen-Sintergewebe"],
    fr: ["Tissage uni", "Tissage sergé", "Hollandais uni", "Fritté 5 couches"],
    pt: ["Trama lisa", "Trama sarja", "Holandesa lisa", "Sinterizado 5 camadas"]
  },
  primaryApplications: {
    en: ["Pharmaceutical Processing", "Chemical Filtration", "Marine Environment", "Food & Beverage"],
    zh: ["制药工艺过滤", "化工流体过滤", "海洋高盐工况", "食品与饮料加工"],
    es: ["Procesamiento farmacéutico", "Filtración química", "Ambiente marino", "Alimentos y bebidas"],
    de: ["Pharmazeutische Filtration", "Chemische Filtration", "Marine Umgebungen", "Lebensmittel & Getränke"],
    fr: ["Procédés pharmaceutiques", "Filtration chimique", "Environnement marin", "Agroalimentaire"],
    pt: ["Processamento farmacêutico", "Filtração química", "Ambiente marinho", "Alimentos e bebidas"]
  },
  chemicalComposition: [{
    element: "Chromium (Cr)",
    minMax: "16.0 - 18.0%"
  }, {
    element: "Nickel (Ni)",
    minMax: "10.0 - 14.0%"
  }, {
    element: "Molybdenum (Mo)",
    minMax: "2.0 - 3.0%"
  }, {
    element: "Carbon (C)",
    minMax: "≤ 0.03%"
  }, {
    element: "Iron (Fe)",
    minMax: "Balance"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/316L.webp?v=yax8d86qpuj",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/316L.webp?v=ibcrrlayqx-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/316L.webp?v=cztk6ojw4us-wvc-sizes",
  altText: {
    en: "High magnification stainless steel 316L woven wire mesh sample",
    zh: "高倍显微下的 AISI 316L 精密不锈钢编织丝网结构",
    es: "Muestra de malla tejida de acero inoxidable 316L a gran aumento",
    de: "Hochauflösende Aufnahme von Edelstahl 316L Drahtgewebe",
    fr: "Échantillon de toile métallique inox 316L à fort grossissement",
    pt: "Amostra ampliada de malha tecida de aço inoxidável 316L"
  }
}, {
  id: "304l",
  name: "Stainless Steel 304 / 304L",
  grade: "AISI 304L / 1.4307",
  unsNumber: "UNS S30403",
  category: "austenitic",
  prenScore: "~19",
  corrosionRating: {
    en: "Standard High",
    zh: "标准工业级高耐蚀",
    es: "Estándar alto",
    de: "Standard hoch",
    fr: "Standard élevé",
    pt: "Padrão elevado"
  },
  keyFeatures: {
    en: ["Industry standard economical alloy", "Excellent ductility and weave precision", "High general atmospheric durability"],
    zh: ["工业通用经济型耐蚀不锈钢", "良好的延展性与精细编织均匀度", "高综合耐大气及中性介质性能"],
    es: ["Aleación estándar de la industria altamente económica", "Excelente ductilidad y precisión en el tejido", "Gran durabilidad atmosférica general"],
    de: ["Wirtschaftliche Standard-Industrielegierung", "Ausgezeichnete Duktilität und Webpräzision", "Hohe allgemeine atmosphärische Beständigkeit"],
    fr: ["Alliage économique standard de l'industrie", "Excellente ductilité et précision de tissage", "Haute résistance atmosphérique générale"],
    pt: ["Liga padrão econômica da indústria", "Excelente ductilidade e precisão de tecelagem", "Alta durabilidade atmosférica geral"]
  },
  commonWeaves: {
    en: ["Square Mesh", "Plain Weave", "Filter Cloth", "Screen Packs"],
    zh: ["方孔网", "平纹编织", "过滤布", "过滤网片组合"],
    es: ["Malla cuadrada", "Tejido liso", "Tela filtrante", "Discos filtrantes"],
    de: ["Quadratmasche", "Leinwandbindung", "Filtergewebe", "Siebpakete"],
    fr: ["Maille carrée", "Tissage uni", "Toile filtrante", "Packs filtrants"],
    pt: ["Malha quadrada", "Trama lisa", "Tela filtrante", "Discos filtrantes"]
  },
  primaryApplications: {
    en: ["Water Filtration", "Industrial Sieving", "Architectural Wire Cloth", "Plastic Recycling"],
    zh: ["工业水处理过滤", "工业固液筛分", "建筑装饰网", "废塑料挤出回收"],
    es: ["Filtración de agua", "Tamizado industrial", "Malla arquitectónica", "Reciclaje de plástico"],
    de: ["Wasserfiltration", "Industrielle Siebung", "Architekturgewebe", "Kunststoffrecycling"],
    fr: ["Filtration de l'eau", "Criblage industriel", "Toile architecturale", "Recyclage plastique"],
    pt: ["Filtração de água", "Peneiramento industrial", "Telas arquitetônicas", "Reciclagem plástica"]
  },
  chemicalComposition: [{
    element: "Chromium (Cr)",
    minMax: "17.5 - 19.5%"
  }, {
    element: "Nickel (Ni)",
    minMax: "8.0 - 10.5%"
  }, {
    element: "Manganese (Mn)",
    minMax: "≤ 2.0%"
  }, {
    element: "Carbon (C)",
    minMax: "≤ 0.03%"
  }, {
    element: "Iron (Fe)",
    minMax: "Balance"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/ss-304-304L.webp?v=3rsejsnfjxc",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/ss-304-304L.webp?v=baigull4lp-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/ss-304-304L.webp?v=eefppxi4z9e-wvc-sizes",
  altText: {
    en: "304L precision square weave stainless wire mesh roll",
    zh: "304L 精密方孔不锈钢编织丝网卷料",
    es: "Rollo de malla de alambre tejida cuadrada de acero inoxidable 304L",
    de: "Rolle aus präzisem Quadratdrahtgewebe Edelstahl 304L",
    fr: "Rouleau de toile métallique carrée en inox 304L",
    pt: "Rolo de tela de arame de aço inoxidável 304L em malha quadrada"
  }
}, {
  id: "hastelloy-c276",
  name: "Hastelloy C-276",
  grade: "Alloy C-276 / 2.4819",
  unsNumber: "UNS N10276",
  category: "nickel",
  prenScore: "~68",
  corrosionRating: {
    en: "Extreme Duty",
    zh: "极重负荷极端工况",
    es: "Servicio extremo",
    de: "Extrembeanspruchung",
    fr: "Service extrême",
    pt: "Serviço extremo"
  },
  keyFeatures: {
    en: ["Immune to wet chlorine gas & hypochlorite", "Outstanding crevice and pitting resistance", "Resists strong oxidizers and mineral acids"],
    zh: ["对湿氯气、次氯酸盐具有优异耐受性", "极高的抗缝隙腐蚀与点蚀性能（PREN ~68）", "耐受强氧化性介质与热无机酸"],
    es: ["Inmune al gas de cloro húmedo e hipoclorito", "Excepcional resistencia a la corrosión por picaduras y rendijas", "Resiste oxidantes fuertes y ácidos minerales"],
    de: ["Beständig gegen feuchtes Chlorgas und Hypochlorit", "Hervorragende Loch- und Spaltkorrosionsbeständigkeit", "Beständig gegen starke Oxidationsmittel und Mineralsäuren"],
    fr: ["Immunisé contre le chlore humide et les hypochlorites", "Résistance exceptionnelle aux piqûres et cavernes", "Résiste aux oxydants puissants et aux acides minéraux"],
    pt: ["Imune a gás cloro úmido e hipoclorito", "Excelente resistência a pites e frestas", "Resiste a fortes oxidantes e ácidos minerais"]
  },
  commonWeaves: {
    en: ["Plain Dutch Weave", "Twill Dutch Weave", "Custom Fine Mesh"],
    zh: ["平纹荷兰编织", "斜纹荷兰编织", "定制微孔精密网"],
    es: ["Tejido Dutch liso", "Tejido Dutch cruzado", "Malla fina a medida"],
    de: ["Glatte Tresse", "Köpertresse", "Kundenspezifisches Feingewebe"],
    fr: ["Hollandais uni", "Hollandais sergé", "Toile fine sur mesure"],
    pt: ["Trama holandesa lisa", "Trama holandesa sarja", "Malha fina sob medida"]
  },
  primaryApplications: {
    en: ["Petrochemical Reactors", "Flue Gas Desulfurization", "Pulp & Paper Bleaching", "Aggressive Acid Filtration"],
    zh: ["石化反应器滤芯", "烟气脱硫洗涤", "纸浆与造纸氯漂白", "强酸与腐蚀性介质过滤"],
    es: ["Reactores petroquímicos", "Desulfuración de gases", "Blanqueo de celulosa", "Filtración de ácidos agresivos"],
    de: ["Petrochemische Reaktoren", "Rauchgasentschwefelung", "Zellstoffbleiche", "Aggressive Säurefiltration"],
    fr: ["Réacteurs pétrochimiques", "Désulfuration des fumées", "Blanchiment papetier", "Filtration d'acides agressifs"],
    pt: ["Reatores petroquímicos", "Dessulfurização de gases", "Branqueamento de celulose", "Filtração de ácidos agressivos"]
  },
  chemicalComposition: [{
    element: "Nickel (Ni)",
    minMax: "Balance (~57%)"
  }, {
    element: "Molybdenum (Mo)",
    minMax: "15.0 - 17.0%"
  }, {
    element: "Chromium (Cr)",
    minMax: "14.5 - 16.5%"
  }, {
    element: "Iron (Fe)",
    minMax: "4.0 - 7.0%"
  }, {
    element: "Tungsten (W)",
    minMax: "3.0 - 4.5%"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/hastelloy-C-276.webp?v=i2o7iq29ix",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/hastelloy-C-276.webp?v=h7pl412e2lg-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/hastelloy-C-276.webp?v=wzwsn44q24o-wvc-sizes",
  altText: {
    en: "Hastelloy C-276 high nickel alloy wire mesh weave",
    zh: "Hastelloy C-276 高镍合金精密丝网编织",
    es: "Malla de alambre de alta aleación de níquel Hastelloy C-276",
    de: "Hastelloy C-276 Nickelbasislegierung Drahtgewebe",
    fr: "Toile tissée en alliage de nickel Hastelloy C-276",
    pt: "Tela de arame em liga de alto níquel Hastelloy C-276"
  }
}, {
  id: "310s",
  name: "Stainless Steel 310S",
  grade: "AISI 310S / 1.4845",
  unsNumber: "UNS S31008",
  category: "austenitic",
  prenScore: "~25",
  corrosionRating: {
    en: "Excellent",
    zh: "优良",
    es: "Excelente",
    de: "Ausgezeichnet",
    fr: "Excellent",
    pt: "Excelente"
  },
  keyFeatures: {
    en: ["High continuous service temperature capability", "High chromium & nickel oxidation protection", "Resists thermal cycling and spalling"],
    zh: ["连续工作耐高温性能", "高铬高镍含量形成致密耐高温氧化保护层", "优异的抗热循环形变与氧化皮剥落性能"],
    es: ["Capacidad para servicio continuo a alta temperatura", "Alto contenido de Cr y Ni para protección contra oxidación", "Resiste ciclos térmicos y descascarillado"],
    de: ["Hohe Dauerbetriebstemperatur-Eignung", "Hoher Chrom- und Nickelgehalt gegen Hochtemperaturoxidation", "Beständig gegen Thermoschock und Zunderbildung"],
    fr: ["Capacité de service continu à haute température", "Forte teneur en Cr et Ni contre l'oxydation à chaud", "Résiste aux cycles thermiques et à l'écaillage"],
    pt: ["Capacidade para serviço contínuo em alta temperatura", "Alto cromo e níquel para proteção contra oxidação", "Resiste a ciclagem térmica e desprendimento de carepa"]
  },
  commonWeaves: {
    en: ["Heavy Duty Plain", "Specialty Coarse Mesh", "Sintered Panels"],
    zh: ["重型平纹编织", "耐高温粗目网", "耐热烧结网板"],
    es: ["Tejido liso pesado", "Malla gruesa especial", "Paneles sinterizados"],
    de: ["Schwere Leinwand", "Sonder-Grobmasche", "Sinterplatten"],
    fr: ["Tissage uni lourd", "Maille grossière spéciale", "Panneaux frittés"],
    pt: ["Trama lisa pesada", "Malha grossa especial", "Painéis sinterizados"]
  },
  primaryApplications: {
    en: ["Furnace Conveyors", "Thermal Treatment Baskets", "High Temperature Gas Filtration", "Catalyst Support"],
    zh: ["热处理炉传送带网", "高温淬火退火吊篮", "高温工业废气过滤", "催化剂载体支撑网"],
    es: ["Cintas transportadoras para hornos", "Cestas de tratamiento térmico", "Filtración de gas a alta temperatura", "Soporte de catalizadores"],
    de: ["Ofentransportbänder", "Wärmebehandlungskörbe", "Heißgasfiltration", "Katalysatorträger"],
    fr: ["Convoyeurs de four", "Paniers de traitement thermique", "Filtration de gaz chaud", "Support catalytique"],
    pt: ["Esteiras transportadoras para fornos", "Cestos de tratamento térmico", "Filtração de gases em alta temperatura", "Suporte de catalisador"]
  },
  chemicalComposition: [{
    element: "Chromium (Cr)",
    minMax: "24.0 - 26.0%"
  }, {
    element: "Nickel (Ni)",
    minMax: "19.0 - 22.0%"
  }, {
    element: "Silicon (Si)",
    minMax: "≤ 1.50%"
  }, {
    element: "Carbon (C)",
    minMax: "≤ 0.08%"
  }, {
    element: "Iron (Fe)",
    minMax: "Balance"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/310S.webp",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/310S.webp?v=lalzvj1iq8-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/310S.webp?v=k5dn815npd-wvc-sizes",
  altText: {
    en: "High temperature 310S stainless steel woven wire cloth",
    zh: "耐高温 310S 不锈钢编织丝网",
    es: "Tela metálica tejida de acero inoxidable para alta temperatura 310S",
    de: "Hochtemperaturbeständiges Drahtgewebe aus Edelstahl 310S",
    fr: "Toile métallique tissée en inox 310S haute température",
    pt: "Tela de arame tecida de aço inoxidável 310S para alta temperatura"
  }
}, {
  id: "monel-400",
  name: "Monel 400",
  grade: "Alloy 400 / 2.4360",
  unsNumber: "UNS N04400",
  category: "nickel",
  prenScore: "Hydrofluoric Specialist",
  corrosionRating: {
    en: "Superior",
    zh: "卓越",
    es: "Superior",
    de: "Hervorragend",
    fr: "Supérieur",
    pt: "Superior"
  },
  keyFeatures: {
    en: ["Unmatched resistance to rapidly flowing seawater", "Resistant to hydrofluoric acid & alkalis", "Solid solution nickel-copper alloy toughness"],
    zh: ["对高速流动海水具有极佳抗冲刷腐蚀能力", "耐受氢氟酸、还原性酸及苛性碱溶液", "镍铜固溶体合金具备极高韧性与机械强度"],
    es: ["Incomparable resistencia al agua de mar de flujo rápido", "Resistente al ácido fluorhídrico y álcalis", "Gran tenacidad por solución sólida de níquel-cobre"],
    de: ["Hervorragende Beständigkeit gegen schnell fließendes Meerwasser", "Beständig gegen Flusssäure und Laugen", "Hohe Zähigkeit durch Nickel-Kupfer-Mischkristallgefüge"],
    fr: ["Résistance inégalée à l'eau de mer à fort débit", "Résiste à l'acide fluorhydrique et aux alcalis", "Ténacité élevée d'un alliage nickel-cuivre monophasé"],
    pt: ["Excelente resistência à água do mar em fluxo rápido", "Resistente a ácido fluorídrico e álcalis", "Tenacidade de liga níquel-cobre em solução sólida"]
  },
  commonWeaves: {
    en: ["Plain Weave", "Dutch Weave", "Filter Cartridge Mesh"],
    zh: ["平纹编织", "荷兰编织", "滤芯专用网"],
    es: ["Tejido liso", "Tejido Dutch", "Malla para cartuchos filtrantes"],
    de: ["Leinwandbindung", "Tressengewebe", "Filterkerzengewebe"],
    fr: ["Tissage uni", "Tissage hollandais", "Toile pour cartouches"],
    pt: ["Trama lisa", "Trama holandesa", "Malha para cartuchos filtrantes"]
  },
  primaryApplications: {
    en: ["Marine Seawater Strainers", "Alkylation Units", "Salt Processing", "Brine Filtration"],
    zh: ["海洋船舶海水过滤器", "炼油厂氢氟酸烷基化装置", "盐化工结晶过滤", "高浓度浓盐水过滤"],
    es: ["Filtros marinos de agua de mar", "Unidades de alquilación", "Procesamiento de sal", "Filtración de salmuera"],
    de: ["Seewasserfilter in der Marine", "Alkylierungsanlagen", "Salzverarbeitung", "Solefiltration"],
    fr: ["Crépines marines d'eau de mer", "Unités d'alkylation", "Traitement du sel", "Filtration de saumure"],
    pt: ["Filtros marinhos de água do mar", "Unidades de alquilação", "Processamento de sal", "Filtração de salmoura"]
  },
  chemicalComposition: [{
    element: "Nickel (Ni)",
    minMax: "63.0% min"
  }, {
    element: "Copper (Cu)",
    minMax: "28.0 - 34.0%"
  }, {
    element: "Iron (Fe)",
    minMax: "≤ 2.5%"
  }, {
    element: "Manganese (Mn)",
    minMax: "≤ 2.0%"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/monel-400.webp?v=bg1vbnnqlug",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/monel-400.webp?v=f5mcegbmumb-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/monel-400.webp?v=4683c7nrbgp-wvc-sizes",
  altText: {
    en: "Monel 400 nickel copper alloy precision mesh screen",
    zh: "Monel 400 镍铜合金精密滤网",
    es: "Malla de precisión en aleación de níquel-cobre Monel 400",
    de: "Monel 400 Nickel-Kupfer-Legierung Präzisionsdrahtgewebe",
    fr: "Toile de filtration de précision en Monel 400",
    pt: "Tela de precisão em liga de níquel-cobre Monel 400"
  }
}, {
  id: "titanium-gr2",
  name: "Titanium Grade 2",
  grade: "Ti Grade 2 / 3.7035",
  unsNumber: "UNS R50400",
  category: "specialty",
  prenScore: "Chloride Immune",
  corrosionRating: {
    en: "Extreme Duty",
    zh: "极重负荷极端工况",
    es: "Servicio extremo",
    de: "Extrembeanspruchung",
    fr: "Service extrême",
    pt: "Serviço extremo"
  },
  keyFeatures: {
    en: ["Completely immune to natural seawater corrosion", "Lightweight with superior strength-to-weight ratio", "Biocompatible non-toxic metallic structure"],
    zh: ["在天然海水与氯化物环境中近乎完全耐蚀", "密度低、比强度高，大幅减轻设备自重", "生物相容性好、无毒性金属结构"],
    es: ["Completamente inmune a la corrosión por agua de mar", "Ligero con excelente relación resistencia-peso", "Estructura metálica biocompatible y no tóxica"],
    de: ["Vollständig korrosionsbeständig gegen natürliches Meerwasser", "Geringes Gewicht mit hohem Festigkeits-Gewichts-Verhältnis", "Biokompatible ungiftige Metallstruktur"],
    fr: ["Totalement résistant à la corrosion en eau de mer", "Légèreté et rapport résistance/poids exceptionnel", "Structure métallique biocompatible non toxique"],
    pt: ["Totalmente imune à corrosão por água do mar", "Leve com excelente relação resistência-peso", "Estrutura metálica biocompatível e não tóxica"]
  },
  commonWeaves: {
    en: ["Fine Plain Weave", "Specialty Expanded Metal", "Micro Filter Mesh"],
    zh: ["精密平纹编织", "特种微孔滤网", "微米级过滤网"],
    es: ["Tejido liso fino", "Metal desplegado especial", "Micro malla filtrante"],
    de: ["Feine Leinwandbindung", "Präzisions-Streckmetall", "Mikro-Filtergewebe"],
    fr: ["Tissage uni fin", "Métal déployé spécialisé", "Micro toile filtrante"],
    pt: ["Trama lisa fina", "Metal expandido especial", "Micro tela filtrante"]
  },
  primaryApplications: {
    en: ["Seawater Desalination", "Anode Basket Lining", "Aerospace Filtration", "Medical Equipment"],
    zh: ["海水淡化工程", "电镀阳极篮包衬网", "航空航天介质过滤", "医疗及生物制药设备"],
    es: ["Desalinización de agua de mar", "Revestimiento de cestas anódicas", "Filtración aeroespacial", "Equipos médicos"],
    de: ["Meerwasserentsalzung", "Anodenkorb-Auskleidung", "Luft- und Raumfahrtfiltration", "Medizintechnik"],
    fr: ["Dessalement d'eau de mer", "Chemise de paniers anodiques", "Filtration aérospatiale", "Équipements médicaux"],
    pt: ["Dessalinização de água do mar", "Revestimento de cestos anódicos", "Filtração aeroespacial", "Equipamentos médicos"]
  },
  chemicalComposition: [{
    element: "Titanium (Ti)",
    minMax: "99.2% min"
  }, {
    element: "Iron (Fe)",
    minMax: "≤ 0.30%"
  }, {
    element: "Oxygen (O)",
    minMax: "≤ 0.25%"
  }, {
    element: "Carbon (C)",
    minMax: "≤ 0.08%"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/titanium.webp?v=zqft1iitv0g",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/titanium.webp?v=xliriid7xtb-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/titanium.webp?v=34pdfy79ps-wvc-sizes",
  altText: {
    en: "Titanium Grade 2 fine wire mesh screen for chemical filtration",
    zh: "用于化工过滤的 Titanium Grade 2 纯钛精密编织丝网",
    es: "Malla de alambre fino de titanio Grado 2 para filtración química",
    de: "Titan Grade 2 Feindrahtgewebe für die chemische Filtration",
    fr: "Toile métallique fine en titane grade 2 pour la filtration chimique",
    pt: "Tela de arame fino de titânio Grau 2 para filtração química"
  }
}, {
  id: "inconel-625",
  name: "Inconel 625",
  grade: "Alloy 625 / 2.4856",
  unsNumber: "UNS N06625",
  category: "nickel",
  prenScore: "~51",
  corrosionRating: {
    en: "Extreme Duty",
    zh: "极重负荷极端工况",
    es: "Servicio extremo",
    de: "Extrembeanspruchung",
    fr: "Service extrême",
    pt: "Serviço extremo"
  },
  keyFeatures: {
    en: ["High strength without heat treatment required", "Resists wide range of severely corrosive fluids", "High fatigue and thermal shock resistance"],
    zh: ["无需沉淀硬化热处理即具备超高屈服强度", "耐受极宽泛的强腐蚀性流体介质", "优良的抗疲劳与耐热冲击开裂性能"],
    es: ["Alta resistencia mecánica sin necesidad de tratamiento térmico", "Resiste una amplia gama de fluidos severamente corrosivos", "Alta resistencia a la fatiga y choque térmico"],
    de: ["Hohe Festigkeit ohne Wärmebehandlung", "Beständig gegen stark korrosive Medien", "Hohe Ermüdungs- und Thermoschockbeständigkeit"],
    fr: ["Haute résistance sans traitement thermique requis", "Résiste à une large gamme de fluides très corrosifs", "Haute résistance à la fatigue et aux chocs thermiques"],
    pt: ["Alta resistência sem necessidade de tratamento térmico", "Resiste a ampla variedade de fluidos altamente corrosivos", "Alta resistência à fadiga e choques térmicos"]
  },
  commonWeaves: {
    en: ["Multi-Layer Sintered", "Plain Dutch Weave", "Heavy Strainer Cloth"],
    zh: ["多层烧结复合网", "平纹荷兰编织", "重型粗滤网布"],
    es: ["Sinterizado multicapa", "Tejido Dutch liso", "Tela filtrante pesada"],
    de: ["Mehrlagen-Sintergewebe", "Glatte Tresse", "Schweres Filtergewebe"],
    fr: ["Fritté multicouche", "Hollandais uni", "Toile de dégrillage lourde"],
    pt: ["Multicamadas sinterizadas", "Trama holandesa lisa", "Tela de retenção pesada"]
  },
  primaryApplications: {
    en: ["Offshore Oil & Gas", "Nuclear Power Systems", "Chemical Waste Processing", "High Pressure Filters"],
    zh: ["海上油气开采", "核电系统工况", "危险化工废液处理", "超高压高压差过滤器"],
    es: ["Petróleo y gas offshore", "Sistemas nucleares", "Tratamiento de residuos químicos", "Filtros de alta presión"],
    de: ["Offshore Öl & Gas", "Kernkraftsysteme", "Chemische Abfallbehandlung", "Hochdruckfilter"],
    fr: ["Pétrole et gaz offshore", "Centrales nucléaires", "Traitement des déchets chimiques", "Filtres haute pression"],
    pt: ["Óleo e gás offshore", "Sistemas nucleares", "Processamento de resíduos químicos", "Filtros de alta pressão"]
  },
  chemicalComposition: [{
    element: "Nickel (Ni)",
    minMax: "58.0% min"
  }, {
    element: "Chromium (Cr)",
    minMax: "20.0 - 23.0%"
  }, {
    element: "Molybdenum (Mo)",
    minMax: "8.0 - 10.0%"
  }, {
    element: "Niobium (Nb)",
    minMax: "3.15 - 4.15%"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/inconel-625.webp",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/inconel-625.webp?v=hdjsktd4jbp-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/inconel-625.webp?v=1s8crrba6tb-wvc-sizes",
  altText: {
    en: "Inconel 625 superalloy fine mesh filter screen",
    zh: "Inconel 625 超级耐热镍基合金精密滤网",
    es: "Malla filtrante fina en superaleación Inconel 625",
    de: "Inconel 625 Superlegierung Feindrahtfiltergewebe",
    fr: "Toile filtrante fine en superalliage Inconel 625",
    pt: "Tela filtrante fina em superliga Inconel 625"
  }
}, {
  id: "904l",
  name: "Super Austenitic 904L",
  grade: "AISI 904L / 1.4539",
  unsNumber: "UNS N08904",
  category: "austenitic",
  prenScore: "~36",
  corrosionRating: {
    en: "Superior",
    zh: "卓越",
    es: "Superior",
    de: "Hervorragend",
    fr: "Supérieur",
    pt: "Superior"
  },
  keyFeatures: {
    en: ["Copper added specifically for sulfuric acid service", "High resistance to pitting in warm chloride solutions", "Fully austenitic non-magnetic structure"],
    zh: ["添加 1.2-2.0% 铜专门抵抗不同浓度硫酸腐蚀", "在温氯化物介质中具备极佳抗点蚀性能（PREN ~36）", "全奥氏体组织，加工硬化后仍保持极低磁导率"],
    es: ["Cobre añadido específicamente para servicio con ácido sulfúrico", "Alta resistencia a picaduras en soluciones templadas de cloruros", "Estructura austenítica completa no magnética"],
    de: ["Kupferzusatz für Beständigkeit gegen Schwefelsäure", "Hohe Lochfraßbeständigkeit in warmen Chloridlösungen", "Vollaustenitisches nichtmagnetisches Gefüge"],
    fr: ["Cuivre ajouté pour le service avec acide sulfurique", "Forte résistance aux piqûres dans les chlorures tièdes", "Structure entièrement austénitique non magnétique"],
    pt: ["Cobre adicionado para serviço com ácido sulfúrico", "Alta resistência a pites em soluções mornas de cloreto", "Estrutura totalmente austenítica não magnética"]
  },
  commonWeaves: {
    en: ["Reverse Dutch Weave", "Fine Filter Cloth", "Screen Packs"],
    zh: ["反向荷兰编织", "精密过滤网布", "组合过滤网包"],
    es: ["Tejido Dutch inverso", "Tela filtrante fina", "Packs de mallas"],
    de: ["Umgekehrte Tresse", "Feines Filtergewebe", "Siebpakete"],
    fr: ["Hollandais inversé", "Toile filtrante fine", "Packs filtrants"],
    pt: ["Trama holandesa inversa", "Tela filtrante fina", "Discos e pacotes"]
  },
  primaryApplications: {
    en: ["Sulfuric Acid Scrubbing", "Phosphoric Acid Production", "Fertilizer Processing", "Pulp Washer Screens"],
    zh: ["硫酸洗涤塔过滤", "湿法磷酸生产分离", "复合化肥生产", "造纸纸浆漂洗筛网"],
    es: ["Lavado de ácido sulfúrico", "Producción de ácido fosfórico", "Procesamiento de fertilizantes", "Lavado de pulpa"],
    de: ["Schwefelsäure-Wäscher", "Phosphorsäureproduktion", "Düngemittelverarbeitung", "Zellstoffwascher"],
    fr: ["Lavage d'acide sulfurique", "Production d'acide phosphorique", "Engrais chimiques", "Laveurs de pâte"],
    pt: ["Lavagem de ácido sulfúrico", "Produção de ácido fosfórico", "Processamento de fertilizantes", "Lavadores de celulose"]
  },
  chemicalComposition: [{
    element: "Iron (Fe)",
    minMax: "Balance (~45%)"
  }, {
    element: "Nickel (Ni)",
    minMax: "24.0 - 26.0%"
  }, {
    element: "Chromium (Cr)",
    minMax: "19.0 - 21.0%"
  }, {
    element: "Molybdenum (Mo)",
    minMax: "4.0 - 5.0%"
  }, {
    element: "Copper (Cu)",
    minMax: "1.2 - 2.0%"
  }],
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/10/904L.webp?v=gfwz2x9pni4",
  imageSrcSet: "https://mutualfilter.com/wp-content/uploads/2026/10/904L.webp?v=h77v5xczpg8-wvc-srcset",
  imageSizes: "https://mutualfilter.com/wp-content/uploads/2026/10/904L.webp?v=p4v0tl8dee-wvc-sizes",
  altText: {
    en: "904L super austenitic stainless steel wire mesh element",
    zh: "904L 超级奥氏体不锈钢编织丝网滤元",
    es: "Elemento de malla de alambre de acero inoxidable super austenítico 904L",
    de: "Superaustenitisches Drahtgewebeelement aus Edelstahl 904L",
    fr: "Élément en toile métallique inox super austénitique 904L",
    pt: "Elemento de tela em aço inoxidável super austenítico 904L"
  }
}];
const gridCopy = {
  eyebrow: {
    en: "Industrial Material Engineering",
    zh: "工业材料工程",
    es: "Ingeniería de materiales industriales",
    de: "Industrielle Werkstofftechnik",
    fr: "Ingénierie des matériaux industriels",
    pt: "Engenharia de materiais industriais"
  },
  heading: {
    en: "Stainless Steels & Specialty Alloys",
    zh: "不锈钢与特种耐蚀合金牌号",
    es: "Aceros inoxidables y aleaciones especiales",
    de: "Edelstähle & Sonderlegierungen",
    fr: "Aciers inoxydables et alliages spéciaux",
    pt: "Aços inoxidáveis e ligas especiais"
  },
  lead: {
    en: "Engineered wire mesh woven from certified mill-test grades. Select the ideal alloy for severe chemical corrosion, high-temperature oxidation, and precision particle retention.",
    zh: "选用符合材料标准的合金材料编织丝网。针对强酸腐蚀、高温氧化与精密截留工况，提供优化的合金选型支持。",
    es: "Malla de alambre diseñada y tejida a partir de calidades certificadas de fábrica. Seleccione la aleación ideal para corrosión química severa, oxidación a alta temperatura y retención precisa de partículas.",
    de: "Präzisionsdrahtgewebe gefertigt aus zertifizierten Werkstoffen. Wählen Sie die optimale Legierung für aggressive chemische Medien, Hochtemperaturoxidation und präzisen Partikelrückhalt.",
    fr: "Toiles métalliques tissées à partir de nuances certifiées d'usine. Sélectionnez l'alliage optimal pour la corrosion chimique sévère, l'oxydation à haute température et la rétention de particules.",
    pt: "Telas de arame tecidas a partir de graus certificados de usina. Selecione a liga ideal para corrosão química severa, oxidação em alta temperatura e retenção precisa de partículas."
  },
  tabAll: {
    en: "All Materials",
    zh: "全部材料",
    es: "Todos los materiales",
    de: "Alle Werkstoffe",
    fr: "Tous les matériaux",
    pt: "Todos os materiais"
  },
  tabAustenitic: {
    en: "Austenitic SS",
    zh: "奥氏体不锈钢",
    es: "Inox austenítico",
    de: "Austenitische Edelstähle",
    fr: "Inox austénitique",
    pt: "Inox austenítico"
  },
  tabNickel: {
    en: "Nickel Alloys",
    zh: "镍基合金",
    es: "Aleaciones de níquel",
    de: "Nickelbasislegierungen",
    fr: "Alliages de nickel",
    pt: "Ligas de níquel"
  },
  tabSpecialty: {
    en: "Specialty & Reactive",
    zh: "特种与活性金属",
    es: "Especiales y reactivos",
    de: "Sonder- & Reaktivmetalle",
    fr: "Spéciaux et réactifs",
    pt: "Especiais e reativos"
  },
  keySpec: {
    en: "Key Specification",
    zh: "关键技术参数",
    es: "Especificación clave",
    de: "Wichtigste Spezifikation",
    fr: "Spécification clé",
    pt: "Especificação principal"
  },
  btnViewSpec: {
    en: "View Chemical Spec",
    zh: "查看化学成分规格",
    es: "Ver especificación química",
    de: "Chemische Spezifikation ansehen",
    fr: "Voir spécification chimique",
    pt: "Ver especificação química"
  },
  corrosionClass: {
    en: "Corrosion Rating",
    zh: "耐腐蚀等级",
    es: "Clase de corrosión",
    de: "Korrosionsklasse",
    fr: "Classe de corrosion",
    pt: "Classe de corrosão"
  },
  datasheetTitle: {
    en: "Technical Datasheet",
    zh: "技术参数表",
    es: "Ficha técnica",
    de: "Technisches Datenblatt",
    fr: "Fiche technique",
    pt: "Ficha técnica"
  },
  datasheetDesc: {
    en: "Chemical composition, temperature tolerance, and primary filtration applications for",
    zh: "化学成分组成、耐温极限及典型过滤应用：",
    es: "Composición química, tolerancia de temperatura y aplicaciones principales de filtración para",
    de: "Chemische Zusammensetzung, Temperaturbeständigkeit und Filtrationsanwendungen für",
    fr: "Composition chimique, tolérance en température et applications principales pour",
    pt: "Composição química, tolerância de temperatura e aplicações principais para"
  },
  prenRating: {
    en: "PREN Rating",
    zh: "抗点蚀指数 (PREN)",
    es: "Índice PREN",
    de: "PREN-Wert",
    fr: "Indice PREN",
    pt: "Índice PREN"
  },
  chemTableTitle: {
    en: "Standard Chemical Composition (%)",
    zh: "标准化学成分百分比 (%)",
    es: "Composición química estándar (%)",
    de: "Standardmäßige chemische Zusammensetzung (%)",
    fr: "Composition chimique standard (%)",
    pt: "Composição química padrão (%)"
  },
  thElement: {
    en: "Element",
    zh: "元素",
    es: "Elemento",
    de: "Element",
    fr: "Élément",
    pt: "Elemento"
  },
  thMass: {
    en: "Mass Percentage Range",
    zh: "质量百分比范围",
    es: "Rango porcentual en masa",
    de: "Massenanteil in %",
    fr: "Pourcentage massique",
    pt: "Faixa percentual em massa"
  },
  appTitle: {
    en: "Key Industrial Applications",
    zh: "重点工业应用领域",
    es: "Aplicaciones industriales principales",
    de: "Wichtigste Industrieanwendungen",
    fr: "Principales applications industrielles",
    pt: "Principais aplicações industriais"
  },
  btnDownloadPdf: {
    en: "Download Full Spec PDF",
    zh: "下载完整规格 PDF",
    es: "Descargar PDF de especificación",
    de: "Vollständiges Datenblatt (PDF)",
    fr: "Télécharger la fiche complète (PDF)",
    pt: "Baixar PDF da especificação"
  },
  bottomEyebrow: {
    en: "Material Certification & Quality Assurance",
    zh: "材料质保与质量检验",
    es: "Certificación de material y aseguramiento de calidad",
    de: "Materialzertifizierung & Qualitätssicherung",
    fr: "Certification matière et assurance qualité",
    pt: "Certificação de material e garantia da qualidade"
  },
  bottomHeading: {
    en: "Need Material Verification or Custom Alloy Weaving?",
    zh: "需要材料检验报告或特种合金编织？",
    es: "¿Requiere verificación de material o tejido en aleaciones especiales?",
    de: "Benötigen Sie Materialprüfberichte oder Sonderlegierungsgewebe?",
    fr: "Besoin de vérification matière ou de tissage d'alliages spéciaux ?",
    pt: "Precisa de verificação de material ou tecelagem de ligas especiais?"
  },
  bottomDesc: {
    en: "Certified Mill Test Reports (EN 10204 3.1) and PMI positive material identification are available when specified in the order. Custom alloys available upon request.",
    zh: "钢厂材质证明报告（EN 10204 3.1）和便携式 XRF PMI 正材光谱分析数据可按订单要求提供。亦可咨询定制特种合金规格。",
    es: "Los informes de ensayo de fábrica (EN 10204 3.1) y la identificación positiva de material PMI están disponibles cuando se especifiquen en el pedido. Aleaciones especiales bajo pedido.",
    de: "Werksprüfzeugnisse nach EN 10204 3.1 und RFA-PMI-Materialprüfungen sind auftragsbezogen auf Anfrage erhältlich. Sonderlegierungen auf Anfrage lieferbar.",
    fr: "Les certificats d'usine (EN 10204 3.1) et le contrôle PMI sont disponibles lorsqu'ils sont spécifiés à la commande. Alliages spéciaux sur demande.",
    pt: "Relatórios de ensaio de fábrica (EN 10204 3.1) e identificação positiva de material PMI estão disponíveis quando especificados no pedido. Ligas especiais sob consulta."
  },
  btnPmi: {
    en: "PMI Verification Standard",
    zh: "PMI 材质检验标准",
    es: "Norma de verificación PMI",
    de: "PMI-Prüfstandard",
    fr: "Norme de contrôle PMI",
    pt: "Padrão de verificação PMI"
  },
  btnCorrosionMatrix: {
    en: "Corrosion Resistance Matrix",
    zh: "耐腐蚀性能对照矩阵",
    es: "Matriz de resistencia a la corrosión",
    de: "Korrosionsbeständigkeitsmatrix",
    fr: "Matrice de résistance à la corrosion",
    pt: "Matriz de resistência à corrosão"
  },
  btnRequestCustom: {
    en: "Request Custom Spec",
    zh: "咨询定制规格",
    es: "Solicitar especificación a medida",
    de: "Sonderanfertigung anfragen",
    fr: "Demander une spécification sur mesure",
    pt: "Solicitar especificação sob medida"
  }
};
export default function MaterialsGrid() {
  const {
    lang
  } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialSpec | null>(null);
  const filteredMaterials = activeCategory === "all" ? MATERIALS_DATA : MATERIALS_DATA.filter(item => item.category === activeCategory);
  return <section id="materialsgrid" data-nav="light" className="mf-section bg-muted/50 text-foreground border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-card text-foreground mf-caption font-semibold uppercase tracking-wider mb-4 border border-border">
              <Microscope className="size-3.5 text-primary" />
              <span>{pick(lang, gridCopy.eyebrow)}</span>
            </div>
            <h2 className="mf-h2 text-foreground font-sans">
              {pick(lang, gridCopy.heading)}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
              {pick(lang, gridCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Category Tabs / Filters */}
        <Reveal delay={60}>
          <div className="mt-8 flex justify-start">
            <Tabs defaultValue="all" value={activeCategory} onValueChange={setActiveCategory} className="w-full max-w-2xl">
              <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 h-auto p-1 bg-card rounded-lg border border-border">
                <TabsTrigger value="all" className="py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, gridCopy.tabAll)} ({MATERIALS_DATA.length})
                </TabsTrigger>
                <TabsTrigger value="austenitic" className="py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, gridCopy.tabAustenitic)}
                </TabsTrigger>
                <TabsTrigger value="nickel" className="py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, gridCopy.tabNickel)}
                </TabsTrigger>
                <TabsTrigger value="specialty" className="py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, gridCopy.tabSpecialty)}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </Reveal>

        {/* Materials Grid: flat title + one line + key spec */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMaterials.map((item, i) => <Reveal key={item.id} delay={Math.min(i * 60, 300)}>
              <Card data-index={i} className="border-border bg-card rounded-lg flex flex-col justify-between overflow-hidden h-full">
                <div>
                  {/* Image Header with hairline border */}
                  <div className="mf-card-media relative mf-ratio-card w-full">
                    <img src={item.imageSrc} data-wvc-srcset={item.imageSrcSet} data-wvc-sizes={item.imageSizes} alt={pick(lang, item.altText)} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                    <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                      
                    </div>
                  </div>

                  <CardHeader className="pb-3">
                    <CardTitle className="mf-h3 font-sans text-card-foreground">
                      {item.name}
                    </CardTitle>
                    <CardDescription className="mf-caption font-mono text-muted-foreground">
                      {item.unsNumber}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4 pb-4">
                    {/* One-sentence summary */}
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, item.keyFeatures)[0]}
                    </p>

                    {/* Key spec strip */}
                    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground mf-caption uppercase tracking-wider font-mono">
                          {pick(lang, gridCopy.prenRating)}
                        </span>
                        <span className="font-semibold text-foreground font-mono">
                          {item.prenScore}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground mf-caption uppercase tracking-wider font-mono">
                          {pick(lang, gridCopy.corrosionClass)}
                        </span>
                        <span className="font-semibold text-foreground">
                          {pick(lang, item.corrosionRating)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-3 pb-4 border-t border-border flex gap-2 justify-between items-center bg-card">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full text-sm font-semibold gap-1.5 h-11" onClick={() => setSelectedMaterial(item)}>
                        <Info className="size-4" />
                        {pick(lang, gridCopy.btnViewSpec)}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                      {selectedMaterial && <>
                          <DialogHeader>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="font-mono mf-caption">
                                {selectedMaterial.unsNumber}
                              </Badge>
                              <Badge variant="outline" className="bg-muted text-foreground mf-caption border border-border">
                                {pick(lang, selectedMaterial.corrosionRating)} {pick(lang, gridCopy.corrosionClass)}
                              </Badge>
                            </div>
                            <DialogTitle className="mf-h2 font-sans mt-2">
                              {selectedMaterial.name} {pick(lang, gridCopy.datasheetTitle)}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-muted-foreground">
                              {pick(lang, gridCopy.datasheetDesc)} {selectedMaterial.grade}.
                            </DialogDescription>
                          </DialogHeader>

                          <div className="space-y-6 my-2">
                            {/* Quick Spec Metrics Grid */}
                            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg text-sm border border-border">
                              <div>
                                <span className="text-muted-foreground block mf-caption font-mono uppercase">
                                  {pick(lang, gridCopy.prenRating)}
                                </span>
                                <span className="font-semibold font-mono text-foreground mt-0.5 block">
                                  {selectedMaterial.prenScore}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block mf-caption font-mono uppercase">
                                  {pick(lang, gridCopy.corrosionClass)}
                                </span>
                                <span className="font-semibold text-foreground mt-0.5 block">
                                  {pick(lang, selectedMaterial.corrosionRating)}
                                </span>
                              </div>
                            </div>

                            {/* Metallurgy highlights */}
                            <div className="space-y-2">
                              <h3 className="text-sm font-semibold text-foreground">
                                {pick(lang, selectedMaterial.altText)}
                              </h3>
                              <ul className="space-y-1.5">
                                {pick(lang, selectedMaterial.keyFeatures).map((feat, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" />
                                    <span>{feat}</span>
                                  </li>)}
                              </ul>
                            </div>

                            {/* Chemical Composition Table */}
                            <div>
                              <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                                <Microscope className="size-4 text-primary" /> {pick(lang, gridCopy.chemTableTitle)}
                              </h3>
                              <div className="border border-border rounded-md overflow-x-auto">
                                <Table>
                                  <TableHeader className="bg-muted">
                                    <TableRow className="border-b border-border">
                                      <TableHead className="text-sm font-semibold text-foreground">
                                        {pick(lang, gridCopy.thElement)}
                                      </TableHead>
                                      <TableHead className="text-sm font-semibold text-right text-foreground">
                                        {pick(lang, gridCopy.thMass)}
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {selectedMaterial.chemicalComposition.map((comp, cIdx) => <TableRow key={cIdx} className="text-sm border-b border-border">
                                        <TableCell className="font-medium text-foreground py-2.5">{comp.element}</TableCell>
                                        <TableCell className="font-mono text-right text-muted-foreground py-2.5">{comp.minMax}</TableCell>
                                      </TableRow>)}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>

                            {/* Recommended Applications */}
                            <div>
                              <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                                <Gauge className="size-4 text-primary" /> {pick(lang, gridCopy.appTitle)}
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {pick(lang, selectedMaterial.primaryApplications).map((app, aIdx) => <div key={aIdx} className="flex items-center gap-2 text-sm p-2.5 rounded bg-card border border-border">
                                    <ShieldCheck className="size-4 text-primary shrink-0" />
                                    <span className="text-foreground">{app}</span>
                                  </div>)}
                              </div>
                            </div>
                          </div>

                          <div className="pt-4 border-t border-border flex flex-col sm:flex-row gap-2 justify-end">
                            <Button variant="outline" asChild className="h-11">
                              <Link to="/DatasheetsPage">
                                <FileText className="size-4 mr-1.5" />
                                {pick(lang, gridCopy.btnDownloadPdf)}
                              </Link>
                            </Button>
                            <Button asChild className="h-11">
                              <Link to="/RequestAQuotePage">
                                {pick(lang, COMMON.requestQuote)}
                                <ArrowRight className="size-4 ml-1.5" />
                              </Link>
                            </Button>
                          </div>
                        </>}
                    </DialogContent>
                  </Dialog>
                </CardFooter>
              </Card>
            </Reveal>)}
        </div>

        {/* Bottom Auxiliary Links & Verification Box */}
        <Reveal delay={120}>
          <div className="mt-12 rounded-lg bg-card border border-border p-6 sm:p-8 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center lg:text-left max-w-2xl">
              <div className="inline-flex items-center gap-1.5 mf-caption font-semibold text-foreground uppercase tracking-wider">
                <ShieldCheck className="size-4 text-primary" />
                <span>{pick(lang, gridCopy.bottomEyebrow)}</span>
              </div>
              <h3 className="mf-h3 text-foreground font-sans">
                {pick(lang, gridCopy.bottomHeading)}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {pick(lang, gridCopy.bottomDesc)}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full lg:w-auto">
              <Button variant="outline" className="w-full sm:w-auto text-sm font-semibold h-11" asChild>
                <Link to="/StainlessSteelMaterialVerificationPage">
                  {pick(lang, gridCopy.btnPmi)}
                </Link>
              </Button>
              <Button variant="outline" className="w-full sm:w-auto text-sm font-semibold h-11" asChild>
                <Link to="/CorrosionResistanceMatricesPage">
                  {pick(lang, gridCopy.btnCorrosionMatrix)}
                </Link>
              </Button>
              <Button className="w-full sm:w-auto text-sm font-semibold h-11" asChild>
                <Link to="/RequestAQuotePage">
                  {pick(lang, gridCopy.btnRequestCustom)}
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}